<template>
  <transition name="ann-fade">
    <div v-if="current" class="ann-mask" @click.self="onClose">
      <div class="ann-card">
        <div class="ann-head">
          <span class="ann-tag">{{ $t("announceTag") }}</span>
          <button class="ann-x" type="button" :aria-label="$t('close')" @click="onClose">×</button>
        </div>

        <h3 class="ann-title">{{ current.title || $t("announceTag") }}</h3>
        <div v-if="timeText" class="ann-time">{{ timeText }}</div>

        <!-- 富文本内容，已消毒后 v-html 渲染 -->
        <div class="ann-content" v-html="safeContent"></div>

        <div class="ann-foot">
          <span v-if="queueLen > 1" class="ann-more">{{ moreText }}</span>
          <button class="ann-btn" type="button" @click="onClose">{{ $t("gotIt") }}</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { msgState, markRead, dismiss, sanitizeHtml } from "../ws";

export default {
  name: "AnnouncementPopup",
  i18n: {
    zh: {
      announceTag: "公告",
      close: "关闭",
      gotIt: "我知道了",
      morePrefix: "还有 ",
      moreSuffix: " 条",
    },
    en: {
      announceTag: "Announcement",
      close: "Close",
      gotIt: "Got it",
      morePrefix: "",
      moreSuffix: " more",
    },
  },
  computed: {
    current() {
      return msgState.queue.length ? msgState.queue[0] : null;
    },
    queueLen() {
      return msgState.queue.length;
    },
    moreText() {
      return `${this.$t("morePrefix")}${this.queueLen - 1}${this.$t("moreSuffix")}`;
    },
    safeContent() {
      return this.current ? sanitizeHtml(this.current.content) : "";
    },
    timeText() {
      const it = this.current;
      if (!it) return "";
      const raw = it.send_time || it.createtime;
      if (!raw) return "";
      let ts = Number(raw);
      if (Number.isFinite(ts) && ts > 0) {
        if (ts < 1e12) ts *= 1000; // 秒 → 毫秒
        const d = new Date(ts);
        if (!Number.isNaN(d.getTime())) return d.toLocaleString();
      }
      return String(raw);
    },
  },
  methods: {
    onClose() {
      const it = this.current;
      if (!it) return;
      markRead(it.id); // 回传服务器标记已读
      dismiss(it.id); // 从弹窗队列移除，展示下一条
    },
  },
};
</script>

<style scoped>
.ann-mask {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(4, 6, 14, 0.68);
  backdrop-filter: blur(6px);
}

.ann-card {
  width: 100%;
  max-width: 360px;
  max-height: 78vh;
  display: flex;
  flex-direction: column;
  border-radius: var(--pool-r, 18px);
  border: 1px solid var(--pool-line, rgba(150, 167, 214, 0.14));
  background: linear-gradient(160deg, #10162c, var(--pool-ink-2, #0b1022));
  box-shadow: 0 24px 60px -24px rgba(0, 0, 0, 0.8);
  overflow: hidden;
}

.ann-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.ann-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  color: #04211c;
  background: var(--pool-grad, linear-gradient(120deg, #33e2c7, #4b86ff));
}

.ann-x {
  border: 0;
  background: transparent;
  color: var(--pool-muted, #828daa);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}

.ann-title {
  margin: 12px 16px 0;
  font-size: 17px;
  font-weight: 700;
  color: var(--pool-text, #eaf0ff);
  line-height: 1.4;
}

.ann-time {
  margin: 6px 16px 0;
  font-size: 12px;
  color: var(--pool-faint, #5a637f);
}

.ann-content {
  margin: 12px 16px;
  padding-right: 4px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.7;
  color: var(--pool-muted, #b8c0d6);
  word-break: break-word;
}
.ann-content ::v-deep img,
.ann-content ::v-deep video,
.ann-content ::v-deep table {
  max-width: 100%;
  height: auto;
}
.ann-content ::v-deep a {
  color: var(--pool-teal, #33e2c7);
  word-break: break-all;
}
.ann-content ::v-deep p {
  margin: 0 0 8px;
}

.ann-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px 16px;
  border-top: 1px solid var(--pool-line, rgba(150, 167, 214, 0.14));
}

.ann-more {
  font-size: 12px;
  color: var(--pool-faint, #5a637f);
}

.ann-btn {
  margin-left: auto;
  height: 40px;
  padding: 0 22px;
  border: 0;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  color: #04211c;
  cursor: pointer;
  background: var(--pool-grad, linear-gradient(120deg, #33e2c7, #4b86ff));
}

.ann-fade-enter-active,
.ann-fade-leave-active {
  transition: opacity 0.2s ease;
}
.ann-fade-enter,
.ann-fade-leave-to {
  opacity: 0;
}
</style>
